import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { getTimeSlots, formatTime } from '../../utils/timeUtils';
import { ClockIcon, UserGroupIcon } from '@heroicons/react/24/outline';

const DailyScheduledSessions = ({ planningData }) => {
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  if (!planningData) return null;

  const timeSlots = getTimeSlots(6, 21, 30);
  const scheduledSessions = planningData.scheduled_sessions || [];

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Calculate responsive column width to match DailyAvailabilityGrid
  const LABEL_WIDTH = 160; // Fixed width for labels in pixels
  const SIDEBAR_WIDTH = 256; // Sidebar width in pixels (w-64 = 256px)
  const PADDING_BUFFER = 32; // Buffer for scrollbars and padding

  const gridCols = timeSlots.length;
  const maxColumnWidth = Math.floor(
    (windowWidth - SIDEBAR_WIDTH - LABEL_WIDTH - PADDING_BUFFER) / gridCols
  );

  // Ensure minimum column width of 32px for usability, maximum of 80px to prevent overly wide columns
  const columnWidth = Math.max(32, Math.min(80, maxColumnWidth));
  const gridTemplateColumns = `repeat(${gridCols}, ${columnWidth}px)`;

  const getSessionAtTime = (time) => {
    return scheduledSessions.find((session) => {
      const sessionStart = formatTime(session.default_start_time);
      return sessionStart === time;
    });
  };

  const getSessionDuration = (session) => {
    return session.duration || 90;
  };

  const getSessionSpan = (session) => {
    const duration = getSessionDuration(session);
    return Math.ceil(duration / 30); // 30-minute slots
  };

  return (
    <div className="border-t border-gray-200 bg-white">
      <div className="border-b border-gray-200 bg-purple-50 p-2">
        <h3 className="text-sm font-medium text-gray-900">
          Scheduled Sessions ({scheduledSessions.length})
        </h3>
      </div>

      <div className="flex">
        <div
          className="flex flex-shrink-0 items-center border-r border-gray-200 bg-gray-50 p-2 text-sm text-gray-700"
          style={{ width: `${LABEL_WIDTH}px` }}
        >
          Sessions
        </div>
        <div className="min-w-0 flex-1 overflow-hidden">
          <div
            className="relative grid h-full"
            style={{
              gridTemplateColumns,
              minHeight: '48px',
              maxWidth: `${columnWidth * gridCols}px`,
            }}
          >
            {timeSlots.map((time, index) => {
              const session = getSessionAtTime(time);
              const isOccupied = scheduledSessions.some((s) => {
                const sessionStart = formatTime(s.start_time || s.default_start_time);
                const sessionStartIndex = timeSlots.indexOf(sessionStart);
                const sessionSpan = getSessionSpan(s);
                return (
                  index >= sessionStartIndex &&
                  index < sessionStartIndex + sessionSpan &&
                  sessionStartIndex !== -1
                );
              });

              if (session) {
                const sessionSpan = getSessionSpan(session);
                return (
                  <div
                    key={`session-${session.id}-${time}`}
                    className="m-0.5 flex flex-col justify-center rounded-md border border-purple-300 bg-purple-100 p-1"
                    style={{
                      gridColumn: `span ${sessionSpan}`,
                      minHeight: '48px',
                    }}
                  >
                    <div className="flex items-center justify-between">
                      <div className="truncate text-xs font-medium text-purple-900">
                        {session.title}
                      </div>
                      {session.parent_session_id && (
                        <span className="rounded bg-orange-100 px-1 py-0.5 text-xs text-orange-800">
                          Split
                        </span>
                      )}
                    </div>
                    <div className="mt-1 flex items-center text-xs text-purple-700">
                      <ClockIcon className="mr-1 h-3 w-3" />
                      <span>{formatTime(session.default_start_time)}</span>
                    </div>
                    <div className="mt-1 flex items-center text-xs text-purple-700">
                      <UserGroupIcon className="mr-1 h-3 w-3" />
                      <span>
                        {session.crew_count} {session.crew_count === 1 ? 'crew' : 'crews'}
                        {session.assigned_users && session.assigned_users.length > 0 && (
                          <span className="ml-1">• {session.assigned_users.length} assigned</span>
                        )}
                      </span>
                    </div>
                    {session.assigned_crews && session.assigned_crews.length > 0 && (
                      <div className="mt-1 truncate rounded bg-purple-200 px-1 py-0.5 text-xs text-purple-800">
                        {session.assigned_crews.map((c) => c.name).join(', ')}
                      </div>
                    )}
                  </div>
                );
              } else if (!isOccupied) {
                return (
                  <div
                    key={`empty-${time}`}
                    className="min-h-12 border-r border-gray-200 bg-gray-50 p-1 transition-colors last:border-r-0 hover:bg-gray-100"
                  />
                );
              } else {
                // This slot is occupied by a session that started earlier
                return null;
              }
            })}
          </div>
        </div>
      </div>

      {/* Session Details List */}
      {scheduledSessions.length > 0 && (
        <div className="border-t border-gray-200 p-3">
          <h4 className="mb-2 text-xs font-medium text-gray-700">Session Details</h4>
          <div className="space-y-2">
            {scheduledSessions.map((session) => (
              <div
                key={session.id}
                className="flex items-center justify-between rounded bg-gray-50 p-2 text-xs"
              >
                <div className="flex items-center space-x-2">
                  <span className="font-medium text-gray-900">{session.title}</span>
                  {session.parent_session_id && (
                    <span className="rounded bg-orange-100 px-1.5 py-0.5 text-xs text-orange-800">
                      Split Session
                    </span>
                  )}
                  <span className="text-gray-600">{formatTime(session.default_start_time)}</span>
                </div>
                <div className="flex items-center space-x-2">
                  {session.assigned_crews && session.assigned_crews.length > 0 && (
                    <span className="rounded bg-blue-100 px-2 py-1 text-blue-800">
                      {session.assigned_crews.map((c) => c.name).join(', ')}
                    </span>
                  )}
                  {session.assigned_users && session.assigned_users.length > 0 && (
                    <span className="rounded bg-green-100 px-2 py-1 text-green-800">
                      {session.assigned_users.filter((u) => u.role === 'coach').length} coach(es)
                      assigned
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

DailyScheduledSessions.propTypes = {
  planningData: PropTypes.shape({
    scheduled_sessions: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number.isRequired,
        title: PropTypes.string.isRequired,
        start_time: PropTypes.string,
        end_time: PropTypes.string,
        default_start_time: PropTypes.string,
        default_end_time: PropTypes.string,
        duration: PropTypes.number,
        crew_name: PropTypes.string,
        assigned_users: PropTypes.arrayOf(PropTypes.object),
      })
    ),
  }),
};

export default DailyScheduledSessions;
